/**
 * Auth service: the orchestration layer that replaces `auth-login.js`.
 * Exposes three public entry points used by views and legacy shims:
 *   - `registerOrLogin(input)`  — main flow from the login form
 *   - `validateSession()`       — page-load session check
 *   - `logout(reason)`          — controlled sign-out
 *
 * Business rules preserved 1:1 from the legacy implementation:
 *   - 3 failed attempts per phone per day → permanent block
 *   - Terms must be accepted before submission
 *   - Production year must match gov registry (or gov is down)
 *   - Plate must exist in gov registry (or gov is down)
 *   - Admin-created stub records are cleaned and treated as new registrations
 *   - OTP is required for new registrations and cross-device logins
 *
 * Atomic transactions on `phoneIndex` and `plateIndex` ensure two simultaneous
 * registrations can't both claim the same phone/plate.
 */

import { auth, db, ref, ensureAuthReady, getFirebase } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { Auth, Firebase } from '../../app/config.js';
import * as bus from '../../core/eventBus.js';
import { createLogger } from '../../core/logger.js';
import * as alert from '../../core/alert.js';

import { normalize as normalizePhone, isValidMobile } from '../../shared/phone.js';
import { normalize as normalizePlate, isValid as isValidPlate } from '../../shared/plate.js';

import { findVehicle, fetchOwnershipHistory } from '../../data/apis/ckan.js';
import { parseBaalutToISO } from '../../shared/date.js';

import { getDeviceId, authorizeForPlate, setAsLastActive, isDeviceTrustedForPhone } from './device.service.js';
import * as deviceWatcher from './deviceWatcher.js';
import * as otpView from './otp.view.js';

const log = createLogger('auth');

/**
 * @typedef {Object} LoginInput
 * @property {string} phone       Raw user input (any format).
 * @property {string} plate       Raw user input.
 * @property {string} year        Year as string (4 digits) from the dropdown.
 * @property {boolean} termsAccepted
 */

/**
 * @typedef {Object} LoginResult
 * @property {'logged-in' | 'cancelled' | 'error'} status
 * @property {string} [uid]
 * @property {string} [message]   Human-readable error / info (RTL Hebrew).
 */

// ====================================================================
// PUBLIC API
// ====================================================================

/**
 * Decide which screen to show on app startup and transition to it.
 * Mirrors the legacy bootstrap from auth-login.js: if there's a local session
 * AND terms have been accepted, validate it against the server and show home
 * on success / login on failure. Otherwise show login immediately.
 *
 * Calls `window.showLoginScreen` / `window.showHomeScreen` from screen-nav.js
 * — those globals are still legacy code in this phase.
 *
 * @returns {Promise<'home' | 'login'>}
 */
export async function bootstrapInitialScreen() {
  const hasSession = LocalState.isLoggedIn && !!LocalState.userId;
  const mustAcceptTerms = !LocalState.termsAccepted;
  const w = /** @type {any} */ (window);

  if (!hasSession || mustAcceptTerms) {
    log.info('no session → login screen');
    showLoginScreenSafe(w);
    return 'login';
  }

  try {
    const result = await validateSession();
    if (result.valid) {
      log.info('session valid → home screen');
      if (typeof w.showHomeScreen === 'function') w.showHomeScreen();
      return 'home';
    }
    log.info('session invalid (', result.reason, ') → login screen');
    showLoginScreenSafe(w);
    return 'login';
  } catch (err) {
    log.warn('bootstrapInitialScreen: validateSession threw, falling back to login', err);
    showLoginScreenSafe(w);
    return 'login';
  }
}

/**
 * Show login screen — preferring screen-nav.js's `showLoginScreen` when
 * available, falling back to a direct display: block on `#loginContainer`.
 * @param {any} w
 */
function showLoginScreenSafe(w) {
  if (typeof w.showLoginScreen === 'function') {
    w.showLoginScreen();
    return;
  }
  const loginEl = document.getElementById('loginContainer');
  const homeEl = document.getElementById('homeScreen');
  if (homeEl) homeEl.style.display = 'none';
  if (loginEl) loginEl.style.display = 'block';
  document.body.classList.add('auth-login-visible');
}

/**
 * How long a successful `validateSession` is trusted before we re-validate
 * against the server. Within this window cold starts skip the server checks
 * entirely — the user is bounced straight to home. Outside the window we
 * re-run the checks, but transient errors (network, permission) NEVER
 * cause a logout; only an unambiguous "you do not own this account
 * anymore" signal does.
 */
const VALIDATE_CACHE_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Validate the local-storage session and (when stale) the server-side claim.
 *
 * Tolerance principles:
 *   - Network / permission errors → treat as "still valid", retry next time.
 *   - Single-index mismatch → keep valid (one transient blip shouldn't kick).
 *   - Both indices mismatch → soft logout (not hardLogout — deviceId stays).
 *   - device-not-present in `vehicles/{plate}/devices` → soft disconnect.
 *     A trusted-device entry under `trustedDevices/{phone}/{deviceId}`
 *     overrides this (the plate-side record is brittle to admin/replace
 *     cleanups; the phone-side record is the authoritative trust marker).
 *
 * @returns {Promise<{ valid: boolean; reason?: string }>}
 */
export async function validateSession() {
  const uid = LocalState.userId;
  const phone = normalizePhone(LocalState.phone ?? '');
  const plate = normalizePlate(LocalState.plate ?? '');

  // Two valid local-session shapes:
  //   - Full account (legacy 3-step flow):   uid + phone + plate
  //   - Phone-only account (SMS-only flow):  uid + phone        (no vehicle yet)
  // Both must have uid + phone; plate is only required for the device-presence
  // and plate-index checks below, which we skip when no plate is registered.
  if (!uid || !phone) {
    log.info('no local session — showing login');
    return { valid: false, reason: 'missing-local' };
  }
  const hasVehicle = !!plate;

  try { await ensureAuthReady(); } catch (e) { log.warn('ensureAuthReady:', e); }
  const authUid = auth().currentUser?.uid ?? null;

  // Cache short-circuit — skip every server check if we validated recently.
  // The Firebase listeners (deviceWatcher) will still react in real time to
  // any actual server-side revocation, so caching here doesn't expand the
  // window of stale trust meaningfully.
  const lastValidated = LocalState.lastValidatedAt ?? 0;
  if (lastValidated > 0 && Date.now() - lastValidated < VALIDATE_CACHE_MS) {
    log.info('session validated recently — short-circuit');
    if (authUid && hasVehicle) deviceWatcher.start({ uid: authUid, plate });
    bus.emit('auth:ready', { uid: authUid ?? uid });
    return { valid: true };
  }

  // Index match: for full accounts, only fail if BOTH indices mismatch.
  // For phone-only accounts (no plate), the phoneIndex is the sole source of
  // truth — a mismatch there means the account moved elsewhere.
  if (hasVehicle) {
    const [phoneIdxOk, plateIdxOk] = await Promise.all([
      indexMatches(`phoneIndex/${phone}`, uid),
      indexMatches(`plateIndex/${plate}`, uid),
    ]);
    if (!phoneIdxOk && !plateIdxOk) {
      log.warn('local session invalid (both indexes mismatch)');
      await logout('index-mismatch');
      return { valid: false, reason: 'index-mismatch' };
    }
  } else {
    const phoneIdxOk = await indexMatches(`phoneIndex/${phone}`, uid);
    if (!phoneIdxOk) {
      log.warn('phone-only session invalid (phoneIndex mismatch)');
      await logout('phone-index-mismatch');
      return { valid: false, reason: 'phone-index-mismatch' };
    }
  }

  const deviceId = getDeviceId();
  if (authUid && deviceId) {
    try {
      const activeId = (await ref(`users/${authUid}/lastActiveDevice/deviceId`).once('value')).val();
      if (activeId && activeId !== deviceId) {
        log.warn('server shows another active device — logging out');
        await logout('another-device');
        return { valid: false, reason: 'another-device' };
      }
    } catch (e) {
      // network/permission hiccup — don't kick out
      log.warn('active-device pre-check failed:', e);
    }
  }

  if (deviceId && hasVehicle) {
    try {
      const present = await ref(`vehicles/${plate}/devices/${deviceId}`).once('value');
      if (!present.exists()) {
        // Vehicles-side device record is missing — could be benign (a recent
        // plate replacement / ownership transfer / admin cleanup). Before
        // disconnecting, check the authoritative trust marker keyed by
        // phone: if THAT exists we know the user is still legitimately
        // trusted from this device.
        const trustedByPhone = await isDeviceTrustedForPhone(phone);
        if (!trustedByPhone) {
          log.warn('device not present under vehicles/devices and not in trustedDevices — disconnect');
          await hardLogout('device-not-present');
          return { valid: false, reason: 'device-not-present' };
        }
        log.info('vehicles/devices missing but trustedDevices confirms — keeping session');
      }
    } catch (e) {
      log.warn('device-presence check failed:', e);
      // Transient — don't logout.
    }
  }

  // Session OK — start watchers + cache the validation timestamp.
  LocalState.lastValidatedAt = Date.now();
  if (authUid && hasVehicle) deviceWatcher.start({ uid: authUid, plate });
  bus.emit('auth:ready', { uid: authUid ?? uid });
  return { valid: true };
}

/**
 * @typedef {Object} PhoneLoginInput
 * @property {string} phone
 * @property {boolean} termsAccepted
 */

/**
 * @typedef {Object} PhoneLoginResult
 * @property {'logged-in' | 'cancelled' | 'error'} status
 * @property {string} [uid]
 * @property {string} [message]
 * @property {boolean} [hasVehicle]   true when the claimed account already has a plate registered
 * @property {string}  [plate]
 * @property {string}  [year]
 */

/**
 * Phone-only login (SMS-only auth flow).
 *
 * After SMS OTP succeeds the client calls the `claimPhoneOwnership` Cloud
 * Function which atomically:
 *   - creates `users/{newUid}` for fresh phones
 *   - migrates `phoneIndex` + `vehicles/{plate}` + indices for cross-device
 *   - purges admin-stub claims
 * Then this function updates `LocalState` with the resulting account info,
 * emits `auth:ready`, and returns `{ hasVehicle }` so the UI can decide
 * whether to gate the search behind a "register your vehicle" prompt.
 *
 * Reuses the existing blocked / attempts / trustedDevices / OTP flow, so the
 * "3 failed attempts" and "skip OTP on trusted device" semantics carry over.
 *
 * @param {PhoneLoginInput} input
 * @returns {Promise<PhoneLoginResult>}
 */
export async function loginWithPhone(input) {
  try {
    if (!input.termsAccepted) {
      return phoneError('חובה לאשר את הסכם השימוש/הפרטיות כדי להמשיך.');
    }
    const phone = normalizePhone(input.phone);
    if (!isValidMobile(phone)) {
      return phoneError('מספר טלפון לא תקין. הזן 05XXXXXXXX או +9725XXXXXXXX');
    }

    await ensureAuthReady();
    const uid = auth().currentUser?.uid;
    if (!uid) return phoneError('שגיאת התחברות. נסה/י לרענן את הדף.');

    // ── Google Play test bypass ──
    // The dedicated review phone (`Auth.testBypass.phone`) is owned by no
    // real human, so SMS OTP is impossible. The legacy `registerOrLogin`
    // required phone+plate+year to match — but the new SMS-only wizard only
    // collects the phone. Match by phone alone here, and synthesise the
    // canonical plate+year from config so the reviewer lands on a working
    // home screen with search enabled.
    //
    // Like `testBypassLogin`, we DO NOT touch `phoneIndex` / `plateIndex` /
    // `vehicles/{plate}` — multiple parallel reviewers each keep their own
    // anonymous uid without overwriting one another.
    if (Auth.testBypass != null && Auth.testBypass.phone === phone) {
      return await testBypassLogin({
        uid,
        phone,
        plate: Auth.testBypass.plate,
        productionYear: Auth.testBypass.year,
      });
    }

    const blocked = await checkBlocked(phone);
    if (blocked) return phoneError(blocked.message);

    const attempts = (await ref(`attempts/${phone}`).once('value')).val();
    if (attempts?.status === 'blocked_permanently') {
      return phoneError(blockedPermanentlyHtml());
    }

    // OTP — skipped if the device is already trusted for this phone (a prior
    // OTP success persists `trustedDevices/{phone}/{deviceId}`).
    const trustedDirect = await isDeviceTrustedForPhone(phone);
    if (!trustedDirect) {
      const otpResult = await otpView.runOtpFlow({ phone });
      if (!otpResult.verified) return { status: 'cancelled' };
    } else {
      log.info('loginWithPhone: device trusted — skipping OTP');
    }

    // Atomic claim via Cloud Function. The function reads `trustedDevices/
    // {phone}/{deviceId}` as proof of phone ownership and performs the
    // fresh-create / same-uid-noop / cross-device-transfer cases under one
    // server-side update.
    const deviceId = getDeviceId();
    let claim;
    try {
      const functions = getFirebase().app().functions(Firebase.functionsRegion);
      const result = await functions.httpsCallable('claimPhoneOwnership')({
        phoneRaw: phone,
        deviceId,
        termsAccepted: true,
      });
      claim = result?.data || {};
    } catch (err) {
      log.error('claimPhoneOwnership failed:', err);
      return phoneError('שגיאה בהתחברות. נסה/י שוב או פנה/י לתמיכה.');
    }

    const hasVehicle = !!claim.hasVehicle;
    const plate = hasVehicle ? normalizePlate(String(claim.plate || '')) : '';
    const year  = hasVehicle ? String(claim.year || '') : '';

    // Persist locally. `saveLocally` requires plate+year — we pass empty
    // strings when there's no vehicle yet (LocalState.plate/year just become
    // empty, which `validateSession`'s no-vehicle branch tolerates).
    saveLocally(uid, phone, plate, year);
    LocalState.termsAccepted = true;
    await ref(`attempts/${phone}`).remove().catch(() => {});

    // Single-device-per-phone enforcement — runs for EVERY login regardless
    // of whether a vehicle is already registered. The `lastActiveDevice` write
    // is what trips the watcher on any other device that's still signed in
    // to this phone. Test-bypass is exempt so multiple Google Play reviewers
    // can be parallel-active without kicking one another out.
    const isTestBypass = Auth.testBypass != null && Auth.testBypass.phone === phone;
    if (!isTestBypass) {
      try { await setAsLastActive(uid); }
      catch (e) { log.warn('setAsLastActive failed:', e); }
      // Plate may be empty — deviceWatcher.start tolerates that and only
      // attaches the lastActiveDevice listener in that case.
      deviceWatcher.start({ uid, plate });
    }

    if (hasVehicle) {
      try { await authorizeForPlate(plate); }
      catch (e) { log.warn('authorizeForPlate failed:', e); }
    }
    bus.emit('auth:ready', { uid });
    return { status: 'logged-in', uid, hasVehicle, plate, year };
  } catch (err) {
    log.error('loginWithPhone threw:', err);
    return phoneError('שגיאה: ' + (/** @type {Error} */ (err).message || 'לא ידועה'));
  }
}

/** @param {string} message */
function phoneError(message) {
  return /** @type {PhoneLoginResult} */ ({ status: 'error', message });
}

/**
 * Main login form submit handler.
 * @param {LoginInput} input
 * @returns {Promise<LoginResult>}
 */
export async function registerOrLogin(input) {
  try {
    if (!input.termsAccepted) {
      return error('חובה לאשר את הסכם השימוש/הפרטיות כדי להמשיך.');
    }

    const phone = normalizePhone(input.phone);
    if (!isValidMobile(phone)) {
      return error('מספר טלפון לא תקין. הזן 05XXXXXXXX או +9725XXXXXXXX');
    }

    const plate = normalizePlate(input.plate);
    if (!isValidPlate(plate)) {
      return error('מספר רכב לא תקין. נדרשות 7 או 8 ספרות.');
    }

    const yearInput = String(input.year || '').replace(/\D/g, '');
    if (!/^\d{4}$/.test(yearInput)) {
      return error('יש למלא שנת ייצור (4 ספרות).');
    }

    await ensureAuthReady();
    const uid = auth().currentUser?.uid;
    if (!uid) return error('שגיאת התחברות. נסה/י לרענן את הדף.');

    // Earliest possible check — Google Play reviewers should never be blocked
    // by attempts-counter, MOT outages, admin-stub edge cases, or any other
    // server-side state. If the input matches `Auth.testBypass` exactly
    // (phone + plate + year), route them through the dedicated QA path
    // immediately. Year is part of the check so the bypass isn't a phone+
    // plate guessability hole.
    if (
      Auth.testBypass != null &&
      Auth.testBypass.plate === plate &&
      Auth.testBypass.phone === phone &&
      Auth.testBypass.year === yearInput
    ) {
      return await testBypassLogin({ uid, phone, plate, productionYear: yearInput });
    }

    const blocked = await checkBlocked(phone);
    if (blocked) return error(blocked.message);

    const today = new Date().toISOString().split('T')[0];
    const attempts = (await ref(`attempts/${phone}`).once('value')).val();
    if (attempts?.status === 'blocked_permanently') {
      return error(blockedPermanentlyHtml());
    }

    // Gov registry check — production year must match. If the registry is
    // unreachable we proceed in "motIsDown" mode and let the user log in.
    const gov = await checkGov(plate);
    let productionYear = yearInput;
    if (gov.govYear) {
      if (yearInput !== gov.govYear) {
        const info = await recordFailedAttempt(phone, today);
        return error(buildAttemptMessage(
          'שנת הייצור שהוזנה אינה תואמת לרישומי משרד התחבורה.',
          info,
        ));
      }
      productionYear = gov.govYear;
    } else if (!gov.down) {
      // gov said plate-not-found
      const info = await recordFailedAttempt(phone, today);
      return error(buildAttemptMessage(
        'הרכב לא נמצא במאגר משרד התחבורה.',
        info,
      ));
    }
    const motIsDown = gov.down === true;
    const lastOwnership = gov.lastOwnership; // string | null

    const [phoneUid, plateUid, vehicle] = await Promise.all([
      readOnce(`phoneIndex/${phone}`),
      readOnce(`plateIndex/${plate}`),
      readOnce(`vehicles/${plate}`),
    ]);

    // -------- Admin-created stub cleanup --------
    if (vehicle && vehicle.ownerId) {
      const existingOwner = await readOnce(`users/${vehicle.ownerId}`) ?? {};
      if (existingOwner?.createdBy === 'admin') {
        const existingPhone = normalizePhone(existingOwner.phone || vehicle.phone || '');
        const existingPlate = normalizePlate(existingOwner.plate || plate);
        if (existingPhone === phone && existingPlate === plate) {
          log.info('full match against admin stub — cleaning and continuing to real registration');
          await purgeAdminStub(plate, vehicle.ownerId, phone);
          await ref(`attempts/${phone}`).remove().catch(() => {});
          // fall through to the normal registration flow below; clear vehicle
          // so subsequent checks treat it as a fresh registration.
          // @ts-ignore
          (/** @type {any} */ (Object))(vehicle).ownerId = null;
        } else if (existingPlate === plate && existingPhone !== phone) {
          return error(`❌ מספר הרכב <strong>${plate}</strong> כבר משויך למספר טלפון אחר במערכת.`);
        } else if (existingPhone === phone && existingPlate !== plate) {
          return error('⚠️ לטלפון זה כבר משויך רכב אחר. החלף/י רכב באזור האישי.');
        }
      }
    }

    // (Test bypass is handled at the top of `registerOrLogin` before any
    // index reads or gov-check side effects.)

    // -------- Phone owned by a different uid --------
    if (phoneUid && phoneUid !== uid) {
      await ref(`attempts/${phone}`).remove().catch(() => {});
      // Two-tier trust check (in order of preference):
      //   1. `trustedDevices/{phone}/{deviceId}` — strongest, anchored to
      //      the phone (set by the verifyOtp Cloud Function). Persists
      //      across vehicle replacements, ownership transfers, admin
      //      cleanups — it represents "the user proved phone ownership
      //      from this device once".
      //   2. `vehicles/{plate}/devices/{deviceId}` — legacy/transitional.
      //      Lets pre-migration users skip OTP if they're still rooted on
      //      the same plate.
      // Either match means we already KNOW the user owns the phone — no
      // need to re-prove via SMS.
      const trustedByPhone = await isDeviceTrustedForPhone(phone);
      const trustedByPlate = trustedByPhone ? true : await isDeviceTrustedForPlate(plate);
      const trusted = trustedByPhone || trustedByPlate;
      if (!trusted) {
        // Cross-device login: require OTP before any data movement.
        const otp = await otpView.runOtpFlow({ phone });
        if (!otp.verified) return { status: 'cancelled' };
      }
      // The phone+plate are indexed under `phoneUid` (the original Firebase
      // auth uid), but the current device is signed in as a different
      // anonymous `uid`. Without migration, `LocalState.userId` would say
      // `phoneUid` while `auth.currentUser.uid` is still `uid` — every
      // subsequent server call (initiateAnonymousCall, rule-protected
      // writes, vehicle.service.replace) would either fail or hit empty
      // data because they all use `auth.uid`.
      //
      // `transferOwnershipToCurrentUser` migrates ownership of the plate
      // to the current auth.uid in a single atomic batch (and, when the
      // old user wasn't an admin stub, removes the dangling old user
      // record). After it runs the new uid owns everything cleanly.
      try {
        const functions = getFirebase().app().functions(Firebase.functionsRegion);
        await functions.httpsCallable('transferOwnershipToCurrentUser')({
          phoneRaw: phone,
          plateRaw: plate,
          productionYear,
          deviceId: getDeviceId(),
        });
        log.info('cross-uid ownership transfer completed → new uid =', uid);
      } catch (err) {
        log.error('transferOwnershipToCurrentUser failed:', err);
        return error('שגיאה בהעברת החשבון. נסה/י שוב או פנה/י לתמיכה.');
      }
      // Log in under the CURRENT auth.uid — the migration just made it
      // the rightful owner.
      return await loginAsExisting({
        uid, phone, plate, productionYear, lastOwnership,
        authorizeDevice: !trusted,
      });
    }

    // -------- Existing owner re-login (same uid) --------
    let ownerUid = /** @type {string | null} */ (null);
    if (phoneUid && plateUid && phoneUid === plateUid) ownerUid = phoneUid;
    else if (phoneUid && vehicle?.ownerId === phoneUid && String(vehicle.phone) === phone) ownerUid = phoneUid;
    if (ownerUid) {
      await ref(`attempts/${phone}`).remove().catch(() => {});
      return await loginAsExisting({ uid: ownerUid, phone, plate, productionYear, lastOwnership });
    }

    // -------- Phone owned by current uid, no vehicle yet — guide to "replace plate" --------
    if (phoneUid && phoneUid === uid) {
      return error('למספר טלפון זה כבר משויך רכב. ניתן להחליף רכב רק דרך האזור האישי (הרכב שלי → החלף רכב רשום).');
    }

    // -------- Plate owned by someone else --------
    if (vehicle?.ownerId && String(vehicle.phone) !== phone) {
      return error(ownershipTakenHtml());
    }

    // -------- Plate owned by current uid (rare — leftover) --------
    if (vehicle?.ownerId === uid) {
      return await loginAsExisting({ uid, phone, plate, productionYear, lastOwnership });
    }

    // -------- Fresh registration: require OTP (unless device is trusted) --------
    // Even though no `phoneIndex` claim exists for this phone, the device
    // may have a `trustedDevices/{phone}/{deviceId}` record from an earlier
    // OTP success (e.g. the account was reset / orphan-cleaned, or this is
    // a returning test-bypass user). In that case the device has already
    // proved phone ownership — no need to re-send SMS.
    const trustedDirect = await isDeviceTrustedForPhone(phone);
    if (!trustedDirect) {
      const otpResult = await otpView.runOtpFlow({ phone });
      if (!otpResult.verified) return { status: 'cancelled' };
    } else {
      log.info('fresh-registration path skipped OTP: device trusted for phone');
    }

    // Re-read in case state changed during OTP wait.
    const recheck = await readOnce(`vehicles/${plate}`);
    if (recheck?.ownerId && String(recheck.phone) !== phone) {
      return error(ownershipTakenHtml());
    }

    const result = await completeRegistration({ uid, phone, plate, productionYear, lastOwnership });
    if (motIsDown) {
      // Best-effort notice; legacy code showed `showInlineNotice` — we use a toast.
      alert.showToast('שירות משרד התחבורה אינו זמין כרגע. הרישום נמשך במצב מוגבל.', { kind: 'warn', duration: 5000 });
    }
    return result;
  } catch (err) {
    log.error('registerOrLogin failed:', err);
    return error('שגיאה: ' + (/** @type {Error} */ (err).message || 'לא ידועה'));
  }
}

/**
 * Sign out cleanly: stop watchers, clear local state, sign out of Firebase.
 * @param {string} [reason]
 */
export async function logout(reason = 'manual') {
  log.info('logout:', reason);
  deviceWatcher.stop();
  bus.emit('auth:logged-out', undefined);
  // Use clearAll (not clearSession) so plate / carYear / fcmToken / search
  // caches are wiped too. clearSession leaves stale vehicle data behind that
  // bleeds into the next login. Matches the hardLogout behaviour and the
  // legacy logout's new key list — single source of truth for "what survives
  // a logout" is now LocalState.KEEP_KEYS (deviceId / phone / termsAccepted).
  LocalState.clearAll();
  try { await auth().signOut(); } catch (e) { log.warn('signOut failed:', e); }
}

/**
 * Last-resort logout: clears everything and reloads. Use when the device has
 * been revoked or the server state is fundamentally inconsistent.
 * @param {string} reason
 */
export async function hardLogout(reason) {
  log.warn('hardLogout (soft):', reason);
  deviceWatcher.stop();
  // `clearAll` now preserves deviceId / phone / termsAccepted (see
  // localState.js::KEEP_KEYS) so the user doesn't lose their trusted-device
  // status on a forced logout.
  LocalState.clearAll();
  try { sessionStorage.clear(); } catch { /* ignore */ }
  try { await auth().signOut(); } catch { /* ignore */ }
  bus.emit('auth:logged-out', undefined);
  // Surface the login screen without a page reload — the reload was the
  // source of the "the app keeps restarting" UX complaint.
  const w = /** @type {any} */ (window);
  try { w.showLoginScreen?.(); } catch { /* ignore */ }
}

// ====================================================================
// INTERNALS
// ====================================================================

/**
 * @param {string} path
 * @param {string} expectedUid
 */
async function indexMatches(path, expectedUid) {
  try {
    const snap = await ref(path).once('value');
    return snap.val() === expectedUid;
  } catch {
    return true; // transient — don't fail
  }
}

/**
 * @template T
 * @param {string} path
 * @returns {Promise<T | null>}
 */
async function readOnce(path) {
  try {
    const snap = await ref(path).once('value');
    return /** @type {T | null} */ (snap.val() ?? null);
  } catch {
    return null;
  }
}

/**
 * Check the three places blocked status can live.
 * @param {string} phone
 * @returns {Promise<{ message: string } | null>}
 */
async function checkBlocked(phone) {
  const support = `\nלפרטים נוספים פנה לתמיכה: ${Auth.supportEmail}`;
  try {
    const blockedSnap = await ref(`blockedUsers/${phone}`).once('value');
    if (blockedSnap.exists()) {
      const reason = blockedSnap.val()?.reason || 'הפרת תנאי שימוש';
      return { message: `חשבונך נחסם מהמערכת.\nסיבה: ${reason}${support}` };
    }
    const delSnap = await ref('deletionLogs').orderByChild('phone').equalTo(phone).once('value');
    if (delSnap.exists()) {
      /** @type {any} */
      let data = null;
      delSnap.forEach((/** @type {any} */ s) => { data = s.val(); });
      return { message: `חשבון זה הוסר מהמערכת ולא ניתן להירשם מחדש.\nסיבה: ${data?.reason || 'מחיקה על ידי מנהל'}${support}` };
    }
    const uid = (await ref(`phoneIndex/${phone}`).once('value')).val();
    if (uid) {
      const blocked = (await ref(`users/${uid}/blocked`).once('value')).val();
      if (blocked === true) {
        const reason = (await ref(`users/${uid}/blockReason`).once('value')).val() || 'הפרת תנאי שימוש';
        return { message: `חשבונך נחסם מהמערכת.\nסיבה: ${reason}${support}` };
      }
    }
    return null;
  } catch (e) {
    log.warn('checkBlocked error:', e);
    return null;
  }
}

/**
 * Check the gov registry for the plate. Returns the gov-reported production
 * year (so the form can adopt it), the ISO of the most recent ownership
 * record, and a `down` flag for when the registry is unreachable.
 *
 * @param {string} plate
 * @returns {Promise<{ govYear: string | null; lastOwnership: string | null; down?: boolean }>}
 */
async function checkGov(plate) {
  try {
    const { records } = await findVehicle(plate);
    if (records.length === 0) {
      return { govYear: null, lastOwnership: null }; // plate not found
    }
    const r = records[0];
    const govYear = r?.shnat_yitzur ? String(r.shnat_yitzur) : null;
    let lastOwnership = /** @type {string | null} */ (null);
    try {
      const history = await fetchOwnershipHistory(plate);
      if (history.length > 0) {
        lastOwnership = parseBaalutToISO(history[0]?.baalut_dt) ?? null;
      }
    } catch { /* ignore — ownership history is best-effort */ }
    return { govYear, lastOwnership };
  } catch (e) {
    log.warn('gov check failed:', e);
    return { govYear: null, lastOwnership: null, down: true };
  }
}

/**
 * Record a failed attempt for the phone and possibly block. Returns the
 * count/status so the caller can build a user-facing message that includes
 * remaining attempts.
 *
 * @param {string} phone
 * @param {string} today    YYYY-MM-DD
 * @returns {Promise<{ count: number; remaining: number; blocked: boolean }>}
 */
async function recordFailedAttempt(phone, today) {
  const attemptsRef = ref(`attempts/${phone}`);
  const snap = await attemptsRef.once('value');
  let count = 1;
  let status = '';
  if (snap.exists() && snap.val()?.date === today) {
    count = (snap.val().count || 0) + 1;
    if (count >= Auth.maxAttemptsPerDay) status = 'blocked_permanently';
  }
  await attemptsRef.set({ date: today, count, status });
  const remaining = Math.max(0, Auth.maxAttemptsPerDay - count);
  return { count, remaining, blocked: status === 'blocked_permanently' };
}

/**
 * The canonical "account is blocked" text. Used in two places that must
 * read identically: (1) the moment we cross the threshold (3rd failed
 * attempt) and (2) every subsequent login attempt while still blocked.
 * Keeping the wording the same prevents the "זמנית vs לצמיתות" confusion
 * the user hit before.
 *
 * @returns {string}
 */
function accountBlockedMessage() {
  return (
    `חשבונך נחסם לאחר ${Auth.maxAttemptsPerDay} ניסיונות כושלים. ` +
    `לא ניתן להתחבר עד לפנייה לתמיכה.\n` +
    `לפרטים פנה אל ${Auth.supportEmail}`
  );
}

/**
 * Format an error message with the remaining-attempts suffix the user
 * expects to see. Returns the standard blocked message once the threshold
 * was crossed (so the 3rd-attempt message matches all subsequent ones).
 *
 * @param {string} reason   Short description of why this attempt failed.
 * @param {{ remaining: number; blocked: boolean }} info
 * @returns {string}
 */
function buildAttemptMessage(reason, info) {
  if (info.blocked) {
    return `${reason}\n\n${accountBlockedMessage()}`;
  }
  return `${reason}\n\nנותרו ${info.remaining} ניסיונות מתוך ${Auth.maxAttemptsPerDay}.`;
}

/**
 * Is this device authorised for the given plate? Used to decide whether a
 * cross-device login can skip OTP.
 * @param {string} plate
 */
async function isDeviceTrustedForPlate(plate) {
  try {
    const deviceId = getDeviceId();
    const snap = await ref(`vehicles/${plate}/devices/${deviceId}`).once('value');
    return snap.exists();
  } catch {
    return false;
  }
}

/**
 * Wipe an admin-created stub vehicle record so a real user can register.
 * @param {string} plate
 * @param {string} oldOwnerUid
 * @param {string} newPhone
 */
async function purgeAdminStub(plate, oldOwnerUid, newPhone) {
  /** @param {string} path */
  const safeRemove = async (path) => {
    try { await ref(path).set(null); } catch (e) { log.warn('safeRemove denied @', path, e); }
  };
  let oldPhone = '';
  try {
    const u = (await ref(`users/${oldOwnerUid}`).once('value')).val();
    oldPhone = normalizePhone(u?.phone ?? '');
  } catch { /* ignore */ }

  await safeRemove(`plateIndex/${plate}`);
  if (oldPhone) {
    await safeRemove(`phoneIndex/${oldPhone}`);
    await safeRemove(`phones/${oldPhone}/plates/${plate}`);
  }
  await safeRemove(`users/${oldOwnerUid}/plates/${plate}`);
  await safeRemove(`vehicles/${plate}`);
  try {
    await ref(`users/${oldOwnerUid}`).remove();
  } catch {
    // archive instead if removal not allowed by rules
    try {
      await ref(`users/${oldOwnerUid}/archived`).set(true);
      await ref(`users/${oldOwnerUid}/archivedAtISO`).set(new Date().toISOString());
    } catch (e) { log.warn('archive admin user failed:', e); }
  }
  if (newPhone) {
    const idx = (await ref(`phoneIndex/${newPhone}`).once('value')).val();
    if (idx === oldOwnerUid) await safeRemove(`phoneIndex/${newPhone}`);
  }
  // Tiny breathing room so RTDB propagates removals before the new writes hit.
  await new Promise((r) => setTimeout(r, 250));
}

/**
 * Log in as an already-registered user. Skips OTP. Used when the indices
 * already point to the same UID or the current device is trusted.
 *
 * @param {Object} args
 * @param {string} args.uid
 * @param {string} args.phone
 * @param {string} args.plate
 * @param {string} args.productionYear
 * @param {string | null} args.lastOwnership
 * @param {boolean} [args.authorizeDevice=false]
 * @returns {Promise<LoginResult>}
 */
async function loginAsExisting({ uid, phone, plate, productionYear, lastOwnership, authorizeDevice = false }) {
  saveLocally(uid, phone, plate, productionYear);
  if (authorizeDevice) {
    try {
      await authorizeForPlate(plate);
      await setAsLastActive(uid);
    } catch (e) { log.warn('authorizeForPlate failed:', e); }
  }
  // Refresh `lastOwnership` on the vehicle so the ownership banner stays in sync
  // with gov data on every login, not only on registration.
  if (lastOwnership) {
    cacheLastOwnership(plate, lastOwnership);
    try {
      await ref(`vehicles/${plate}`).update({
        lastOwnership,
        lastOwnershipUpdatedAt: Date.now(),
      });
    } catch (e) { log.warn('vehicle lastOwnership update failed:', e); }
  }
  deviceWatcher.start({ uid, plate });
  bus.emit('auth:ready', { uid });
  return { status: 'logged-in', uid };
}

/**
 * QA-only login path used when the user's input matches `Auth.testBypass`.
 *
 * Designed for the Google Play "App access" reviewers, who:
 *   - cannot receive the SMS OTP (the test phone isn't theirs)
 *   - run automated checks on **multiple devices/emulators in parallel**
 *   - return for re-reviews under fresh anonymous Firebase uids each time
 *
 * To make all three work concurrently we write ONLY `users/{auth.uid}` —
 * no global index claims (`phoneIndex`, `plateIndex`, `vehicles/{plate}`),
 * no `lastActiveDevice` write, no `deviceWatcher` subscription. Each
 * concurrent reviewer thus lives entirely under their own anonymous uid
 * and cannot interfere with the others. The trade-off — the test plate
 * won't be searchable / receive incoming calls — is acceptable for a
 * review session.
 *
 * @param {Object} args
 * @param {string} args.uid
 * @param {string} args.phone
 * @param {string} args.plate
 * @param {string} args.productionYear
 * @returns {Promise<LoginResult>}
 */
async function testBypassLogin({ uid, phone, plate, productionYear }) {
  log.warn('test bypass triggered — skipping OTP, indices, and device watcher');
  const now = Date.now();
  try {
    await ref(`users/${uid}`).update({
      phone, plate, year: productionYear,
      termsAccepted: true,
      termsAcceptedAt: new Date().toISOString(),
      termsVersion: Auth.termsVersion,
      updatedAt: now,
      createdAt: db().ServerValue?.TIMESTAMP ?? now,
      testBypass: true,
    });
  } catch (e) {
    log.error('test-bypass users/{uid} update failed:', e);
    return error('שגיאה בהתחברות לבדיקות.');
  }
  saveLocally(uid, phone, plate, productionYear);
  bus.emit('auth:ready', { uid });
  return { status: 'logged-in', uid };
}

/**
 * Commit a brand-new registration. Writes are NOT atomic — that's a known
 * security/consistency debt that gets fixed when this moves to a Cloud Function.
 *
 * @param {Object} args
 * @param {string} args.uid
 * @param {string} args.phone
 * @param {string} args.plate
 * @param {string} args.productionYear
 * @param {string | null} args.lastOwnership
 * @returns {Promise<LoginResult>}
 */
async function completeRegistration({ uid, phone, plate, productionYear, lastOwnership }) {
  const now = Date.now();
  const termsAcceptedAt = new Date().toISOString();

  // Index claims via transaction so two simultaneous registrations can't both win.
  const phoneClaim = await ref(`phoneIndex/${phone}`).transaction(
    (/** @type {any} */ cur) => (cur === null || cur === uid ? uid : undefined),
  );
  if (!phoneClaim.committed) return error('מספר טלפון זה כבר משויך למשתמש אחר.');

  const plateClaim = await ref(`plateIndex/${plate}`).transaction(
    (/** @type {any} */ cur) => (cur === null || cur === uid ? uid : undefined),
  );
  if (!plateClaim.committed) {
    // rollback phone claim
    try {
      await ref(`phoneIndex/${phone}`).transaction(
        (/** @type {any} */ cur) => (cur === uid ? null : cur),
      );
    } catch (e) { log.warn('phoneIndex rollback failed:', e); }
    return error(ownershipTakenHtml());
  }

  await ref(`users/${uid}`).update({
    phone, plate, year: productionYear,
    termsAccepted: true,
    termsAcceptedAt,
    termsVersion: Auth.termsVersion,
    updatedAt: now,
    createdAt: db().ServerValue?.TIMESTAMP ?? now,
    lastOwnership: lastOwnership ?? null,
  });
  await ref(`vehicles/${plate}`).update({
    ownerId: uid,
    phone, plate, year: productionYear,
    // Mirror lastOwnership onto the vehicle so the ownership banner doesn't
    // show "אי-ודאות" right after registration. Without this the banner reads
    // gov ownership but finds nothing locally and falls into the "partial" branch.
    lastOwnership: lastOwnership ?? null,
    lastOwnershipUpdatedAt: lastOwnership ? now : null,
    updatedAt: now,
    createdAt: db().ServerValue?.TIMESTAMP ?? now,
  });
  await ref(`phones/${phone}/plates/${plate}`).set(true);
  await ref(`users/${uid}/plates/${plate}`).set({ linkedAt: now });
  await ref(`attempts/${phone}`).remove().catch(() => {});

  saveLocally(uid, phone, plate, productionYear);
  try {
    await authorizeForPlate(plate);
    await setAsLastActive(uid);
  } catch (e) { log.warn('authorize device failed:', e); }
  if (lastOwnership) cacheLastOwnership(plate, lastOwnership);

  deviceWatcher.start({ uid, plate });
  bus.emit('auth:ready', { uid });
  return { status: 'logged-in', uid };
}

/**
 * @param {string} uid
 * @param {string} phone
 * @param {string} plate
 * @param {string} year
 */
function saveLocally(uid, phone, plate, year) {
  LocalState.userId = uid;
  LocalState.phone = phone;
  LocalState.plate = plate;
  LocalState.carYear = year;
  LocalState.isLoggedIn = true;
  const now = Date.now();
  LocalState.lastLoginAt = now;
  // A fresh successful login implicitly validates the session, so the next
  // cold start hits the validate cache and doesn't re-query the server.
  LocalState.lastValidatedAt = now;
  LocalState.termsAccepted = true;
}

/**
 * Persist the most recent gov baalut_dt locally so banners can render the
 * "since YYYY-MM" hint without a network round-trip. Mirrors the legacy
 * `lastGovBaalutDt_{plate}` key.
 * @param {string} plate
 * @param {string} iso
 */
function cacheLastOwnership(plate, iso) {
  try {
    const yyyymm = new Date(iso).toISOString().slice(0, 7).replace('-', '');
    if (/^\d{6}$/.test(yyyymm)) {
      localStorage.setItem(`lastGovBaalutDt_${plate}`, yyyymm);
    }
  } catch { /* ignore */ }
}

/** @param {string} message @returns {LoginResult} */
function error(message) { return { status: 'error', message }; }

function ownershipTakenHtml() {
  return (
    'הרכב כבר רשום למשתמש אחר במערכת. ' +
    `אם הרכב עבר לבעלותך, פנה לתמיכה (${Auth.supportEmail}) עם אסמכתא.`
  );
}

function blockedPermanentlyHtml() {
  // Identical wording to the message shown the moment the user crosses
  // the threshold (see `accountBlockedMessage`). Keeps the 3rd-attempt
  // and 4th+ attempt messages consistent.
  return accountBlockedMessage();
}
